/**
 * Pure helpers that map a commit-moment state to its i18n key. Centralized so
 * the commit-hosting pages (ConfirmationPage today, Guarantee/Personalize
 * post-flip) and the hook agree on which strings to show.
 *
 * From T30 / plan `remove-confirm-step.md` "Commit-moment design contract":
 * - D1: Context-aware CTA copy per step × fee status
 * - D4: Commit-error UX with category-specific keys
 */

import type { CommitErrorCategory } from '@core/analytics';

/**
 * Which step is hosting the commit click — passed in so the helper can pick
 * the right CTA. Today only 'confirm' is reachable; 'guarantee' and
 * 'personalize' come online when the flag-flip lands.
 */
export type CommitHostStep = 'confirm' | 'guarantee' | 'personalize';

/**
 * Inputs the CTA-copy helper considers when picking a key.
 *
 * - `hostStep`: which page is hosting the commit click
 * - `hasFee`: true when a payment guarantee will charge the user a non-zero
 *   amount (deposit only, not no_show — see commit 343e594).
 * - `isSubmitting`: when true, returns the in-flight key regardless of other
 *   state.
 * - `hasError`: when true (after a failed attempt), returns the retry key.
 * - `isWaitingList`: when true, the user is joining a waiting list rather
 *   than completing a reservation. The CTA reflects that — "join the waiting
 *   list" instead of "complete your booking". Reachable when
 *   bookingSkipConfirmationV2 is enabled on a WL-flow tenant: the commit
 *   host becomes PERSONALIZE or GUARANTEE while flowType is 'waiting_list'.
 */
export interface CommitCtaInputs {
  hostStep: CommitHostStep;
  hasFee: boolean;
  isSubmitting: boolean;
  hasError: boolean;
  isWaitingList: boolean;
}

/**
 * Return the i18n key for the commit-button label given the current state.
 * Pages pass `{ amount }` to `t()` for `COMMIT_CTA_GUARANTEE_WITH_FEE` and
 * `COMMIT_CTA_WL_GUARANTEE_WITH_FEE`.
 *
 * Precedence: isSubmitting > hasError > isWaitingList > hostStep.
 * Loading/retry stay generic (they describe progress, not outcome). WL takes
 * precedence over per-step keys because the user's intent ("join waiting
 * list") doesn't change with which page is hosting the commit click.
 *
 * All keys live in the `booking` namespace.
 */
export function commitCtaKey(inputs: CommitCtaInputs): string {
  if (inputs.isSubmitting) return 'COMMIT_CTA_LOADING';
  if (inputs.hasError) return 'COMMIT_CTA_RETRY';
  if (inputs.isWaitingList) {
    if (inputs.hostStep === 'guarantee' && inputs.hasFee) {
      return 'COMMIT_CTA_WL_GUARANTEE_WITH_FEE';
    }
    return 'COMMIT_CTA_WL_JOIN';
  }
  if (inputs.hostStep === 'guarantee') {
    return inputs.hasFee ? 'COMMIT_CTA_GUARANTEE_WITH_FEE' : 'COMMIT_CTA_GUARANTEE_NO_FEE';
  }
  // 'personalize' (and 'confirm' for now until the flag flips) → same generic key
  return 'COMMIT_CTA_PERSONALIZE';
}

/**
 * Map a coarse error category (from `categorizeCommitError`) to the matching
 * inline error copy key. Used by the hook to localize the message it puts on
 * `error: string | null`.
 *
 * All keys live in the `errors` namespace.
 *
 * Categories with category-specific copy:
 *   3ds_rejected → COMMIT_ERROR_3DS_REJECTED
 *   card_declined → COMMIT_ERROR_PAYMENT_DECLINED
 *   network → COMMIT_ERROR_NETWORK
 *   backend_blocked + unknown → COMMIT_ERROR_GENERIC
 *
 * Note: backend_blocked could be further differentiated (BLOCKED_USER vs
 * MONTHLY_LIMIT each have their own existing keys in booking namespace),
 * but the page-level UX is the same — generic try-again message above an
 * inline error block. The pre-existing key paths in useReservationCommit
 * for those specific codes still apply when the cause carries the code.
 */
export function commitErrorKey(category: CommitErrorCategory): string {
  switch (category) {
    case '3ds_rejected':
      return 'COMMIT_ERROR_3DS_REJECTED';
    case 'card_declined':
      return 'COMMIT_ERROR_PAYMENT_DECLINED';
    case 'network':
      return 'COMMIT_ERROR_NETWORK';
    case 'backend_blocked':
    case 'unknown':
    default:
      return 'COMMIT_ERROR_GENERIC';
  }
}
